import { useState } from 'react';
import { TrendingUp, DollarSign, Package, Sparkles } from 'lucide-react';
import { Holding } from '../api/portfolio';

interface PortfolioSummaryProps {
  portfolio: Holding[];
}

// Exchange rates (mock - in production, fetch from API)
const EXCHANGE_RATES: Record<string, { rate: number; symbol: string }> = {
  'USD': { rate: 1, symbol: '$' },
  'EUR': { rate: 0.92, symbol: '€' },
  'GBP': { rate: 0.79, symbol: '£' },
  'JPY': { rate: 149.50, symbol: '¥' },
  'CAD': { rate: 1.36, symbol: 'C$' },
  'AUD': { rate: 1.53, symbol: 'A$' },
  'CHF': { rate: 0.88, symbol: 'CHF' },
};

export default function PortfolioSummary({ portfolio }: PortfolioSummaryProps) {
  const [currency, setCurrency] = useState<string>('USD');
  
  // Sum up all holdings using their USD-converted values
  const totalValueUSD = portfolio.reduce((sum, holding) => sum + (holding.totalValueUSD || holding.totalValue || 0), 0);
  const totalHoldings = portfolio.length;
  
  // Convert to selected currency
  const exchangeRate = EXCHANGE_RATES[currency].rate;
  const currencySymbol = EXCHANGE_RATES[currency].symbol;
  const totalValue = totalValueUSD * exchangeRate;

  // Calculate average price
  const avgPrice = totalHoldings > 0 ? totalValue / totalHoldings : 0;
  
  // Calculate mock daily change (in production, this would be real data)
  const dailyChange = totalValue * 0.023; // Mock 2.3% gain
  const dailyChangePercent = 2.3;

  return (
    <div className="space-y-4">
      {/* Currency Selector */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Portfolio Overview</h2>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Currency:</span>
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {Object.keys(EXCHANGE_RATES).map((curr) => (
              <option key={curr} value={curr}>
                {curr} ({EXCHANGE_RATES[curr].symbol})
              </option>
            ))}
          </select>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Total Value */}
        <div className="stat-card group">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Total Value</p>
              <p className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-primary-600 to-blue-600 bg-clip-text text-transparent">
                {currencySymbol}{totalValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <div className="flex items-center gap-1 mt-1">
                <span className="text-xs font-medium text-green-600">
                  +{currencySymbol}{dailyChange.toFixed(2)}
                </span>
                <span className="text-xs text-gray-500">
                  ({dailyChangePercent > 0 ? '+' : ''}{dailyChangePercent}%)
                </span>
              </div>
            </div>
            <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-primary-100 to-blue-100 rounded-xl group-hover:scale-110 transition-transform duration-300">
              <DollarSign className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </div>

      {/* Total Holdings */}
      <div className="stat-card group">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Holdings</p>
            <p className="text-2xl md:text-3xl font-bold text-gray-900">{totalHoldings}</p>
            <p className="text-xs text-gray-500 mt-1">Positions</p>
          </div>
          <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl group-hover:scale-110 transition-transform duration-300">
            <Package className="w-6 h-6 text-blue-600" />
          </div>
        </div>
      </div>

      {/* Average Position */}
      <div className="stat-card group">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Avg Position</p>
            <p className="text-2xl md:text-3xl font-bold text-gray-900">
              ${avgPrice.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </p>
            <p className="text-xs text-gray-500 mt-1">Per holding</p>
          </div>
          <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl group-hover:scale-110 transition-transform duration-300">
            <TrendingUp className="w-6 h-6 text-green-600" />
          </div>
        </div>
      </div>

      {/* Performance Indicator */}
      <div className="stat-card group bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-1">Today</p>
            <p className="text-2xl md:text-3xl font-bold text-green-600">
              +{dailyChangePercent}%
            </p>
            <p className="text-xs text-green-600 mt-1 font-medium">Strong performance</p>
          </div>
          <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-green-200 to-emerald-200 rounded-xl group-hover:scale-110 transition-transform duration-300">
            <Sparkles className="w-6 h-6 text-green-700" />
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
